import type { ClickIdResponse } from './types';

let fetchInFlight: Promise<ClickIdResponse> | null = null;

const safeParseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const isResponseObject = (body: unknown): body is ClickIdResponse => {
  return body !== null && typeof body === 'object' && !Array.isArray(body);
};

const throwClickIdResponseError = (
  message: string,
  details: {
    status: number;
    statusText: string;
    url: string;
    body: unknown;
  },
): never => {
  const error = new Error(message);
  Object.assign(error, details);
  throw error;
};

const validateClickIdResponse = (
  response: Response,
  body: unknown,
  url: string,
  logger: ReturnType<typeof useNuxtApp>['$logger'],
): ClickIdResponse => {
  const details = {
    status: response.status,
    statusText: response.statusText,
    url,
    body,
  };

  if (!response.ok) {
    logger.note({
      category: 'clickID',
      message: 'clickid fetch non-ok response',
      level: 'warning',
      data: { status: response.status, url },
    });
    throwClickIdResponseError(`Click ID fetch failed: ${response.status} ${response.statusText}`, details);
  }

  if (!body) {
    throwClickIdResponseError('Click ID response body is empty', details);
  }

  if (!isResponseObject(body)) {
    throwClickIdResponseError('Click ID response body has unexpected format', details);
  }

  if (body.status === 0) {
    throwClickIdResponseError(body.message || 'RedTrack: click not found', details);
  }

  return body;
};

const fetchData = async (url: string, logger: ReturnType<typeof useNuxtApp>['$logger']): Promise<ClickIdResponse> => {
  const raw = await fetch(url);
  const body = raw.status === 204 || raw.headers.get('Content-Length') === '0'
    ? null
    : await safeParseBody(raw);
  const data = validateClickIdResponse(raw, body, url, logger);

  logger.note({
    category: 'clickID',
    message: 'clickid request sent',
    level: 'debug',
  });

  return data;
};

export const fetchClickIdData = (url: string, logger: ReturnType<typeof useNuxtApp>['$logger']): Promise<ClickIdResponse> => {
  fetchInFlight ??= fetchData(url, logger).finally(() => {
    fetchInFlight = null;
  });

  return fetchInFlight;
};
